/**
 * application-form — pure page-approval logic (application-form-builder spec,
 * task 7.1).
 *
 * This module holds the I/O-free heart of the per-page director-approval axis:
 * the constrained `PageApprovalStatus` value set, the `Approval_Details` a
 * director records on approval, and the lock-on-approve state machine that
 * governs which `(from, to)` transitions a director may apply. It is the single
 * source of truth for both — every page-approval read/write and every status
 * transition the `application-form-service` handler performs routes through
 * here, exactly the `merit-judging/competitionLifecycle.ts` precedent
 * (`is*` / `assert*` value sets + transition guard, never re-declared at the
 * storage or handler layer).
 *
 * Page approval is a SEPARATE, NARROW axis from the application's
 * `Submission_Status` (`applicationStatus`). It is stored per
 * `(applicantPersonId, competitionId, formPageId)` and is determined
 * INDEPENDENTLY of `applicationStatus` (Req 21.6, 16.5) — this module never
 * reads or writes `applicationStatus`. Main application approval is governed
 * elsewhere; nothing here couples the two axes.
 *
 * The state machine is small, total, and "locks on approve":
 *
 *     PENDING ──approve──▶ APPROVED ──(re-approve, idempotent)──▶ APPROVED
 *        │                    ▲
 *        └──reject──▶ REJECTED ┘ (a rejected page may still be approved)
 *
 * The legal transitions are exactly:
 *   - PENDING  → APPROVED   (approve a pending page; Req 21.2, 21.7)
 *   - PENDING  → REJECTED   (reject a pending page; Req 21.2)
 *   - REJECTED → APPROVED   (approve a previously-rejected page; Req 21.2)
 *   - APPROVED → APPROVED   (idempotent re-approve; Req 21.4)
 *
 * Every other `(from, to)` pair is illegal and leaves the stored status
 * UNCHANGED. In particular, once a page is APPROVED it can only stay APPROVED:
 * it can never move back to PENDING or REJECTED, which is what makes the page
 * "locked on approve" so the applicant can no longer edit it (Req 21.4).
 *
 * `Approval_Details` are recorded EXACTLY on the APPROVED transition (Req 21.7)
 * — never on a REJECTED transition — and become the structured information a
 * director can later project into the merit judge view (Req 21.8), decided by
 * `audienceVisibility.ts`.
 *
 * Because the module is pure and dependency-free it is exercised directly by
 * `jest` + `fast-check` (task 7.2) and reused by the handler's
 * `setPageApprovalStatus` op (task 17.7) as the one place that decides whether
 * a transition is allowed.
 *
 * Validates: Requirements 21.2, 21.4, 21.6, 21.7, 16.5
 */

// ---------------------------------------------------------------------------
// Errors

/**
 * Raised for an illegal page-approval transition or an out-of-set status value.
 * Self-contained (this is a wave-0 pure module with no handler yet); the
 * `application-form-service` adapter maps it to a client error when it wires
 * `setPageApprovalStatus` (task 17.7).
 */
export class PageApprovalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PageApprovalError';
  }
}

// ---------------------------------------------------------------------------
// Constrained value set (Req 21.2)

/**
 * The per-applicant approval state of an approval-required Form_Page. Exactly
 * one of these three values, tracked SEPARATELY from the application's
 * `Submission_Status` (Req 21.2, 21.6).
 */
export type PageApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** Every allowed `Page_Approval_Status` value (Req 21.2). */
export const PAGE_APPROVAL_STATUSES: readonly PageApprovalStatus[] = [
  'PENDING',
  'APPROVED',
  'REJECTED',
] as const;

/**
 * The status an approval-required page holds for an applicant before any
 * director decision (Req 21.2). The absence of a stored `PageApproval` row is
 * equivalent to this default.
 */
export const DEFAULT_PAGE_APPROVAL_STATUS: PageApprovalStatus = 'PENDING';

/**
 * The structured information a director records when approving an
 * approval-required Form_Page (Req 21.7). `fields` carries the
 * director-configured detail entries that `audienceVisibility.ts` may project
 * into the merit judge view where the director marks them visible (Req 21.8).
 */
export interface ApprovalDetails {
  /** The director who made the decision (personId / cognitoSub). */
  approvedBy: string;
  /** When the decision was made (ISO 8601). */
  decidedAt: string;
  /** Optional free-text note from the director. */
  note?: string;
  /**
   * Director-configured structured detail fields, surfaced to judges where the
   * director configures them visible (Req 21.7, 21.8).
   */
  fields?: Record<string, string>;
}

/**
 * The legal `Page_Approval_Status` transitions, keyed by source state; the
 * value is the set of permitted targets from that state. The machine
 * locks-on-approve: from APPROVED only APPROVED is reachable (idempotent
 * re-approve), so an approved page can never return to PENDING or REJECTED
 * (Req 21.2, 21.4). Any `(from, to)` pair absent from this map is rejected.
 */
export const LEGAL_PAGE_APPROVAL_TRANSITIONS: Readonly<
  Record<PageApprovalStatus, readonly PageApprovalStatus[]>
> = {
  PENDING: ['APPROVED', 'REJECTED'],
  REJECTED: ['APPROVED'],
  APPROVED: ['APPROVED'],
} as const;

// ---------------------------------------------------------------------------
// Value-set guards (Req 21.2)

/** Whether `value` is a member of the allowed `PageApprovalStatus` set. */
export function isValidPageApprovalStatus(value: unknown): value is PageApprovalStatus {
  return (PAGE_APPROVAL_STATUSES as readonly string[]).includes(value as string);
}

/**
 * Reject a `Page_Approval_Status` value outside its defined set, leaving the
 * record unchanged. On a read this is the "corrupt-row" signal an out-of-set
 * stored status raises (Req 21.2).
 */
export function assertValidPageApprovalStatus(
  value: unknown,
): asserts value is PageApprovalStatus {
  if (!isValidPageApprovalStatus(value)) {
    throw new PageApprovalError(
      `Invalid PageApprovalStatus "${String(value)}"; expected one of ` +
        `${PAGE_APPROVAL_STATUSES.join(' | ')} (Req 21.2).`,
    );
  }
}

// ---------------------------------------------------------------------------
// Transition guard (Req 21.2, 21.4)

/**
 * Whether moving from `from` to `to` is a legal page-approval transition. Both
 * endpoints must be valid statuses; a `(from, to)` pair is legal only when `to`
 * is in the source state's permitted target set. The legal set is exactly
 * PENDING→APPROVED, PENDING→REJECTED, REJECTED→APPROVED, and APPROVED→APPROVED
 * (idempotent re-approve). Every other pair — including PENDING→PENDING,
 * REJECTED→REJECTED, REJECTED→PENDING, APPROVED→PENDING, and APPROVED→REJECTED —
 * is illegal (Req 21.2, 21.4).
 */
export function canTransition(from: unknown, to: unknown): boolean {
  if (!isValidPageApprovalStatus(from) || !isValidPageApprovalStatus(to)) {
    return false;
  }
  return LEGAL_PAGE_APPROVAL_TRANSITIONS[from].includes(to);
}

/**
 * Apply a page-approval transition. Total over the legal input space: it throws
 * `PageApprovalError` for any illegal `(from, to)` pair (and performs no
 * mutation, so the caller leaves the stored status UNCHANGED), and otherwise
 * returns the resulting `{ status, details? }`.
 *
 * `Approval_Details` are recorded EXACTLY on the APPROVED transition (Req 21.7):
 * when `to === 'APPROVED'` the provided `details` (if any) are carried on the
 * result; for the PENDING→REJECTED transition no details are recorded, so any
 * `details` argument is ignored. The endpoints are validated first, so an
 * out-of-set `from`/`to` surfaces as an invalid-status error rather than a
 * silent transition.
 */
export function applyTransition(
  from: unknown,
  to: unknown,
  details?: ApprovalDetails,
): { status: PageApprovalStatus; details?: ApprovalDetails } {
  assertValidPageApprovalStatus(from);
  assertValidPageApprovalStatus(to);

  if (!canTransition(from, to)) {
    throw new PageApprovalError(
      `Illegal PageApprovalStatus transition ${from} → ${to}; the legal ` +
        `transitions are PENDING → APPROVED, PENDING → REJECTED, ` +
        `REJECTED → APPROVED, and APPROVED → APPROVED (Req 21.2, 21.4).`,
    );
  }

  // Approval_Details are recorded exactly on the APPROVED transition (Req 21.7).
  if (to === 'APPROVED') {
    return details === undefined ? { status: to } : { status: to, details };
  }
  return { status: to };
}

// ---------------------------------------------------------------------------
// Applicant edit lock (Req 21.4)

/**
 * Whether the applicant may edit an approval-required Form_Page given its
 * current `Page_Approval_Status`. Editing is permitted in every state EXCEPT
 * APPROVED — i.e. the page is locked to the applicant exactly once it is
 * approved (Req 21.4). This predicate concerns ONLY the page-approval axis and
 * deliberately never consults `applicationStatus` (the two axes are independent;
 * Req 21.6, 16.5).
 */
export function isPageEditableByApplicant(status: PageApprovalStatus): boolean {
  return status !== 'APPROVED';
}
